import { gql } from 'graphql-request';

import {
  baseApi,
  insert,
  select,
  update,
  remove,
  afterRemove,
  afterUpdate,
  afterInsert,
  beforeUpdate,
  buildPaginationClauses,
  objectToStr,
} from './base';

import {
  usersByRole,
  profileGroupType,
  profileType,
  allUsersType,
  notificationProfileType,
  invitationType,
  clientType,
  publicProfileType,
} from '../graphqlTypes';

const companyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Search company users (by company_id), similar to company/project/sheet search
    searchCompanyUsers: select(builder, 'profile', profileType, false, {
      query: (params = {}) => {
        const search = typeof params.search === 'string' ? params.search : '';
        const hasSearch = search.trim().length > 0;
        const disabledOnly = !!params.disabledOnly; // default false
        const companyId =
          (typeof params.companyId === 'number' && Number.isFinite(params.companyId)) ||
          typeof params.companyId === 'string'
            ? params.companyId
            : undefined;

        const andConditions = [];

        if (typeof companyId !== 'undefined') {
          const literal = typeof companyId === 'number' ? companyId : `"${companyId}"`;
          andConditions.push(`{ company_id: { _eq: ${literal} } }`);
        }

        // Disabled filter: when false, include false OR null; when true, only true
        if (disabledOnly) {
          andConditions.push(`{ is_disabled: { _eq: true } }`);
        } else {
          andConditions.push(
            `{ _or: [ { is_disabled: { _eq: false } }, { is_disabled: { _is_null: true } } ] }`
          );
        }

        if (hasSearch) {
          const term = search.replace(/"/g, '\\"');
          const orParts = [
            `{ display_name: { _ilike: "${term}" } }`,
            `{ email: { _ilike: "${term}" } }`,
            `{ company: { name: { _ilike: "${term}" } } }`,
          ];
          andConditions.push(`{ _or: [ ${orParts.join(', ')} ] }`);
        }

        const where = andConditions.length
          ? `where: { _and: [ ${andConditions.join(', ')} ] }`
          : '';
        const order = 'order_by: { display_name: asc }';
        const clauses = [where, order].filter(Boolean);
        const [limitClause, offsetClause] = buildPaginationClauses({
          params,
          hasSearch,
        });
        clauses.push(limitClause);
        clauses.push(offsetClause);
        const finalClauses = clauses.join(', ');
        return {
          body: gql`
            query {
              profile(${finalClauses}) ${profileType}
            }
          `,
        };
      },
    }),
    searchForUsers: select(
      builder,
      'profile',
      `{
          id
          email
          display_name
          is_disabled
          profile_groups(where: {group: {type: {_eq:"personal"}}}) {
            group {
              id
            }
          }
          company_id
        }`,
      false,
      {
        cacheKey: (params) =>
          params?.includeDisabled ? 'searchForUsers-includeDisabled' : 'searchForUsers',
        filter: (params) => {
          const searchClause = `{_or: [{email: {_ilike: "%${params.search}%"}}, {display_name: {_ilike: "%${params.search}%"}}]}`;
          if (params.includeDisabled) return searchClause;
          return `{_and: [
            ${searchClause},
            {_or: [{is_disabled: {_eq: false}}, {is_disabled: {_is_null: true}}]}
          ]}`;
        },
      }
    ),
    searchUsers: builder.query({
      query: (params) => ({
        body: gql`
          query {
            search_users(args: {search: "${params.search}"}
            ${
              params.companies
                ? `, where: {company_id: {_in: [${params.companies.join(',')}]}}`
                : ''
            }, limit: 200)
            ${publicProfileType}
          }`,
      }),
      transformResponse: (response) => response.search_users,
      forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
      serializeQueryArgs: ({ queryArgs }) => queryArgs.cacheKey,
    }),
    getUsersByRole: select(builder, 'profile', usersByRole, false, {
      filter: (params) => `{ role: { _in:[${params.roles}]}}`,
    }),
    addUsersToGroup: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          insert_profile_group(objects: ${objectToStr(
            params.payload.map((p) => ({
              profile_id: p.profileId,
              group_id: p.groupId,
              role: p.role,
            }))
          )}) {
                returning ${profileGroupType}
              }
        }
        `,
      }),
      transformResponse: (response) => response.insert_profile_group.returning,
      onQueryStarted: afterInsert(''),
    }),
    updateProfileGroup: update(builder, 'profile_group', profileGroupType, beforeUpdate('', true)),
    removeProfileGroup: remove(builder, 'profile_group', profileGroupType, afterRemove('')),
    getGroupProfiles: select(builder, 'profile_group', profileGroupType, false, {
      filter: (params) => `{ group_id: {_eq: ${params.group_id}}}`,
      // cacheKey: (params) => `getGroupProfiles("${params?.queryArgs?.group_id}")`,
    }),
    getProfile: builder.query({
      query: (id) => ({
        body: gql`
          query {
            profile_by_pk(id: "${id}") ${profileType}
          }`,
      }),
      transformResponse: (response) => response.profile_by_pk,
      providesTags: ['Profile'],
    }),
    insertProfile: builder.mutation({
      query: (profile) => ({
        body: gql`
        mutation {
          insert_profile_one(object: {id:  ${profile.id}, email:  ${profile.email}, role:  ${profile.role}, display_name:  ${profile.displayName}})
        }
        `,
      }),
      transformResponse: (response) => response.profile_by_pk,
    }),
    updateProfile: update(builder, 'profile', profileType, afterUpdate('getProfile')),
    updateProfilePrivate: builder.mutation({
      query: (params) => {
        const ids = Array.isArray(params.id) ? params.id : [params.id];
        const setFields = [];
        if ('license' in params.payload)
          setFields.push(`license: ${params.payload.license ? `"${params.payload.license}"` : 'null'}`);
        if ('revit_add_in' in params.payload)
          setFields.push(`revit_add_in: ${params.payload.revit_add_in ? `"${params.payload.revit_add_in}"` : 'null'}`);
        return {
          body: gql`
            mutation {
              update_profile_private(where: {id: {_in: [${ids.map(id => `"${id}"`).join(', ')}]}}, _set: {${setFields.join(', ')}}) {
                affected_rows
                returning {
                  id
                  license
                  revit_add_in
                }
              }
            }
          `,
        };
      },
      transformResponse: (response) => response.update_profile_private,
      invalidatesTags: ['Profile'],
    }),
    getAllUsers: select(builder, 'profile', profileType),
    getNotifications: select(builder, 'notification_profile', notificationProfileType, false, {
      order: `{notification: {created_at: desc}}`,
    }),
    deleteNotification: remove(
      builder,
      'notification_profile',
      `{id}`,
      afterRemove('getNotifications')
    ),
    deleteNotifications: builder.mutation({
      query: (params) => {
        const query = gql`
          mutation {
            delete_notification_profile(where: { id: { _in: [${params.ids}] } }) {
              affected_rows
            }
          }
        `;
        return {
          body: query,
        };
      },
      transformResponse: (response) => response.delete_notification_profile,
      invalidatesTags: ['NotificationProfile'],
    }),
    getUsersWithCompany: select(builder, 'profile', profileType, false, {
      filter: (params) => `{company_id: {_eq: ${params.id}}}`,
    }),
    getLatestInvitation: select(builder, 'invitation', invitationType, false, {
      filter: (params) => `{email: {_eq: "${params.email}"}}`,
      order: `{updated_at: desc}`,
      limit: 1,
    }),
    insertInvitation: insert(builder, 'invitation', invitationType),
    updateInviation: update(builder, 'invitation', invitationType),
    insertClient: insert(builder, 'client', clientType),
    exportAllUsers: builder.query({
      query: () => ({
        body: gql`
          query {
            all_users(order_by: { display_name: asc }) ${allUsersType}
          }
        `,
      }),
      transformResponse: (response) => response.all_users,
    }),
  }),
  overrideExisting: false,
});

export const {
  useSearchForUsersQuery,
  useLazySearchForUsersQuery,
  useSearchCompanyUsersQuery,
  useSearchUsersQuery,
  useGetUsersByRoleQuery,
  useAddUsersToGroupMutation,
  useUpdateProfileGroupMutation,
  useRemoveProfileGroupMutation,
  useGetProfileQuery,
  useInsertProfileMutation,
  useUpdateProfileMutation,
  useUpdateProfilePrivateMutation,
  useGetAllUsersQuery,
  useGetUsersWithCompanyQuery,
  useGetNotificationsQuery,
  useDeleteNotificationMutation,
  useDeleteNotificationsMutation,
  useGetLatestInvitationQuery,
  useInsertInvitationMutation,
  useUpdateInviationMutation,
  useInsertClientMutation,
  useGetGroupProfilesQuery,
  useExportAllUsersQuery,
} = companyApi;
